"use client";

import React from "react";
import { Facebook, Instagram, Mail, MapPin, Phone, ShieldCheck, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-3 bg-accent/10 border border-accent/20 rounded-md">
        <p className="text-sm text-accent-foreground font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-3">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className="flex gap-2">
                <Input type="email" placeholder="Enter your email" className="bg-input/30 border-border/50 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary h-10" {...field} />
                <Button type="submit" disabled={isSubmitting} className="h-10 px-4">
                  {isSubmitting ? "..." : "Subscribe"}
                </Button>
              </div>
            </FormControl>
            <FormMessage className="text-destructive-foreground" />
          </FormItem>} />
      {submitError && <p className="text-sm text-destructive-foreground mt-2">{submitError}</p>}
    </Form>;
}
export default function Footer() {
  const socialLinks = [{
    icon: Facebook,
    href: "https://www.facebook.com/yesdentalcenters/",
    label: "Facebook"
  }, {
    icon: Instagram,
    href: "https://www.instagram.com/restoredentalcenters/",
    label: "Instagram"
  }];
  const trustBadges = ["Insurance Accepted", "HIPAA Compliant", "Board Certified Specialists"];
  return <section id="footer" className="dark bg-background text-foreground pt-16 pb-8 px-6 border-t border-border/20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-12">
          
          {}
          <div className="space-y-6">
            <Link to="/">
              <img src="https://solutionsforsleepapnea.com/wp-content/uploads/2026/04/Mask-group-scaled.png" alt="Solutions for Sleep Apnea Logo" className="h-12 w-auto" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Comprehensive diagnosis and personalized treatment options for sleep apnea, including oral appliances, lifestyle changes, and surgical solutions. Dedicated to improving sleep health with compassionate, evidence-based care.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="size-4 shrink-0 text-primary mt-0.5" />
                <span>2002 South Hoover Street <br />Los Angeles, CA 90007</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="size-4 shrink-0 text-primary" />
                <span>(310) 858-7373</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="size-4 shrink-0 text-primary" />
                <span>support@restoredentalcenters.com</span>
              </div>
            </div>
          </div>

          {}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-foreground">Quick Links</h3>
            <MenuProvider menu_id="17">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-foreground">Patient Trust</h3>
            <ul className="space-y-4 mb-6">
              {trustBadges.map((badge, i) => <li key={i} data-index={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ShieldCheck className="size-4 text-accent" />
                  <span>{badge}</span>
                </li>)}
            </ul>
            <div className="space-y-2">
              <Link to="/privacy-policy" className="block text-sm text-muted-foreground hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="block text-sm text-muted-foreground hover:text-primary transition-colors">Terms of Service</Link>
              <Link to="/hipaa-notice" className="block text-sm text-muted-foreground hover:text-primary transition-colors">HIPAA Notice of Privacy Practices</Link>
            </div>
          </div>

          {}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-foreground">Stay Informed</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Subscribe to our newsletter for the latest updates on sleep health and treatment options.
            </p>
            <FormProvider formId="newsletter-form-footer" sectionName="footer" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Thank you for subscribing to our newsletter.">
              <NewsletterForm />
            </FormProvider>

            <div className="mt-8">
              <h4 className="text-sm font-semibold mb-4 text-foreground">Connect With Us</h4>
              <div className="flex gap-4">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={i} data-index={i} to={social.href} aria-label={social.label} className="bg-secondary/50 p-2 rounded-full text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300">
                      <Icon className="size-4" />
                    </Link>;
              })}
              </div>
            </div>
          </div>

        </div>

        <Separator className="bg-border/30 mb-8" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground/70 text-center md:text-left">
          <p className="max-w-3xl">
            <strong>Medical Disclaimer:</strong> The information provided on this website is for educational purposes only and is not intended as a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition.
          </p>
          <p className="shrink-0">
            &copy; {new Date().getFullYear()} Solutions for Sleep Apnea. All rights reserved.
          </p>
        </div>
      </div>
    </section>;
}